import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ClassTemplateStudent {
  code: string;
  nickname: string | null;
  first_name: string;
  last_name: string | null;
}

export interface ClassTemplate {
  id: string;
  teacher_code: string;
  teacher_name: string | null;
  teacher_nickname: string | null;
  weekday: number;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  course_type: string;
  class_type: string;
  education_level: string;
  language: 'THAI' | 'ENGLISH';
  subject: string;
  location: string;
  room_name: string | null;
  is_special_class: boolean;
  fixed_cost: number | null;
  note: string | null;
  status: 'NORMAL' | 'PAUSED';
  is_active: boolean;
  created_at: string;
  updated_at: string;
  students: ClassTemplateStudent[];
}

export function useClassTemplates(weekday?: number, teacherCode?: string) {
  return useQuery({
    queryKey: ['class-templates', weekday, teacherCode],
    queryFn: async () => {
      let query = supabase
        .from('v_class_templates')
        .select('*')
        .order('start_time', { ascending: true });

      if (weekday !== undefined) {
        query = query.eq('weekday', weekday);
      }
      if (teacherCode) {
        query = query.eq('teacher_code', teacherCode);
      }

      const { data, error } = await query;

      if (error) throw error;
      return (data ?? []) as unknown as ClassTemplate[];
    },
    enabled: weekday !== undefined,
  });
}

export function useClassTemplate(id: string | null) {
  return useQuery({
    queryKey: ['class-template', id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from('v_class_templates')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      return data as unknown as ClassTemplate | null;
    },
    enabled: !!id,
  });
}
